import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  curatorTasksApi,
  repeatSummary,
  taskSectionsForPicker,
  type TaskRepeatKind,
  type TaskRule,
} from '@/api/curatorTasks';
import { AudiencePicker } from '@/components/curator/AudiencePicker';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { dayValue } from '@/lib/day';
import { humanizeError } from '@/lib/errors';
import { Modal } from '@/components/ui/Modal';
import { useConfirm } from '@/components/ui/ConfirmProvider';
import { useIsMobile } from '@/hooks/useIsMobile';
import { ActionSheet } from '@/components/mobile/ActionSheet';
import { MAdd, MBlock, MEmpty, MRow, MTag, MTitle } from '@/components/mobile/MobileKit';

/**
 * Задания для списка дел ученика.
 *
 * ⚠️ ЗАДАЁТСЯ ПРАВИЛО, А НЕ ЗАДАНИЕ НА КАЖДЫЙ ДЕНЬ. «Слова по пн/ср/пт до конца
 * марафона» — одна запись; сегодняшний пункт платформа считает сама. Иначе
 * перенос занятия превращался бы в правку сотни строк, а отмена — в мусор.
 */
export function CuratorTasksPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Partial<TaskRule> | null>(null);
  const [sheetFor, setSheetFor] = useState<TaskRule | null>(null);
  const isMobile = useIsMobile();
  const ask = useConfirm();

  const dayNames = [
    t('weekdaysShort.mon'), t('weekdaysShort.tue'), t('weekdaysShort.wed'),
    t('weekdaysShort.thu'), t('weekdaysShort.fri'), t('weekdaysShort.sat'), t('weekdaysShort.sun'),
  ];

  const list = useQuery({ queryKey: ['curator', 'daily-tasks'], queryFn: curatorTasksApi.list });

  const save = useMutation({
    mutationFn: (rule: Partial<TaskRule>) =>
      rule.id ? curatorTasksApi.update(rule.id, rule) : curatorTasksApi.create(rule),
    onSuccess: async () => {
      setEditing(null);
      await qc.invalidateQueries({ queryKey: ['curator', 'daily-tasks'] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => curatorTasksApi.remove(id),
    onSuccess: async () => qc.invalidateQueries({ queryKey: ['curator', 'daily-tasks'] }),
  });

  const rules = list.data ?? [];
  const draft = editing ?? null;

  const setDraft = (patch: Partial<TaskRule>) => setEditing((d) => ({ ...(d ?? {}), ...patch }));

  const toggleWeekday = (day: number) => {
    const current = draft?.weekdays ?? [];
    setDraft({ weekdays: current.includes(day) ? current.filter((d) => d !== day) : [...current, day] });
  };

  const startNew = () =>
    setEditing({ kind: 'section', audience_type: 'group', repeat_kind: 'daily', weekdays: [], is_active: true });
  // ⚠️ ОКНО ПРИЛОЖЕНИЯ, А НЕ `window.confirm` (13.10.2026, docs/CURATOR-MOBILE-DESIGN.md §3, правило 5): системное окно
  // браузера в приложении выглядит чужим и на iOS подписано адресом страницы. На компьютере — то же окно, что у
  // остальных страниц админки.
  const confirmRemove = async (rule: TaskRule) => {
    if (await ask({ message: t('curatorTasks.removeConfirm', { title: rule.title }), danger: true })) remove.mutate(rule.id);
  };
  const summary = (rule: TaskRule) =>
    `${repeatSummary(rule, dayNames, t('curatorTasks.repeatDaily'), t('curatorTasks.repeatOnce'))} · ${t(
      `curatorTasks.audience_${rule.audience_type}`,
    )}`;
  const formTitle = draft?.id ? t('curatorTasks.editTitle') : t('curatorTasks.newTitle');
  const saveButton = draft && (
    <Button onClick={() => save.mutate(draft)} disabled={!draft.title?.trim() || save.isPending}>
      {t('common.save')}
    </Button>
  );
  const cancelButton = (
    <Button variant="ghost" onClick={() => setEditing(null)}>{t('common.cancel')}</Button>
  );

  // Поля формы — одни на компьютер и телефон: на компьютере они под списком на странице, на телефоне — окном во
  // весь экран (правило 3), с «Сохранить» прибитой к низу.
  const fields = draft && (
        <div className="space-y-4">
          <label className="block space-y-1">
            <span className="text-xs font-semibold uppercase tracking-label text-on-surface-variant">
              {t('curatorTasks.fieldTitle')}
            </span>
            <input
              className="w-full rounded-button border border-outline-variant bg-surface px-3 py-2 text-sm"
              value={draft.title ?? ''}
              onChange={(e) => setDraft({ title: e.target.value })}
              placeholder={t('curatorTasks.titlePlaceholder')}
            />
          </label>

          <div className="space-y-1">
            <span className="text-xs font-semibold uppercase tracking-label text-on-surface-variant">
              {t('curatorTasks.fieldKind')}
            </span>
            <div className="flex flex-wrap gap-2">
              {(['section', 'lesson', 'text'] as const).map((kind) => (
                <Button
                  key={kind}
                  variant={draft.kind === kind ? 'primary' : 'ghost'}
                  onClick={() => setDraft({ kind, target: '' })}
                >
                  {t(`curatorTasks.kind_${kind}`)}
                </Button>
              ))}
            </div>
          </div>

          {draft.kind === 'section' && (
            <label className="block space-y-1">
              <span className="text-xs font-semibold uppercase tracking-label text-on-surface-variant">
                {t('curatorTasks.fieldSection')}
              </span>
              {/* Список, а не поле для адреса: опечатка дала бы задание,
                  ведущее в никуда, и заметил бы это ученик. */}
              <select
                className="w-full rounded-button border border-outline-variant bg-surface px-3 py-2 text-sm"
                value={draft.target ?? ''}
                onChange={(e) => setDraft({ target: e.target.value })}
              >
                <option value="">—</option>
                {taskSectionsForPicker(draft.target).map((s) => (
                  <option key={s.target} value={s.target}>{t(s.labelKey)}</option>
                ))}
              </select>
            </label>
          )}

          {draft.kind === 'lesson' && (
            <label className="block space-y-1">
              <span className="text-xs font-semibold uppercase tracking-label text-on-surface-variant">
                {t('curatorTasks.fieldLesson')}
              </span>
              <input
                className="w-full rounded-button border border-outline-variant bg-surface px-3 py-2 text-sm"
                value={draft.target ?? ''}
                onChange={(e) => setDraft({ target: e.target.value })}
                placeholder={t('curatorTasks.lessonPlaceholder')}
              />
            </label>
          )}

          <div className="space-y-1">
            <span className="text-xs font-semibold uppercase tracking-label text-on-surface-variant">
              {t('curatorTasks.fieldAudience')}
            </span>
            {/* Списками, а не идентификатором — см. пояснение в AudiencePicker. */}
            <AudiencePicker value={draft} onChange={(patch) => setDraft(patch)} labelPrefix="curatorTasks" />
          </div>

          <div className="space-y-1">
            <span className="text-xs font-semibold uppercase tracking-label text-on-surface-variant">
              {t('curatorTasks.fieldRepeat')}
            </span>
            <div className="flex flex-wrap gap-2">
              {(['daily', 'weekdays', 'once'] as TaskRepeatKind[]).map((r) => (
                <Button
                  key={r}
                  variant={draft.repeat_kind === r ? 'primary' : 'ghost'}
                  onClick={() => setDraft({ repeat_kind: r })}
                >
                  {t(`curatorTasks.repeat_${r}`)}
                </Button>
              ))}
            </div>

            {draft.repeat_kind === 'weekdays' && (
              <div className="mt-2 flex flex-wrap gap-2">
                {dayNames.map((name, i) => {
                  const day = i + 1; // 1 — понедельник … 7 — воскресенье
                  const on = (draft.weekdays ?? []).includes(day);
                  return (
                    <Button key={day} variant={on ? 'primary' : 'ghost'} onClick={() => toggleWeekday(day)}>
                      {name}
                    </Button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block space-y-1">
              <span className="text-xs font-semibold uppercase tracking-label text-on-surface-variant">
                {t('curatorTasks.fieldStarts')}
              </span>
              <input
                type="date"
                className="w-full rounded-button border border-outline-variant bg-surface px-3 py-2 text-sm"
                value={dayValue(draft.starts_on)}
                onChange={(e) => setDraft({ starts_on: e.target.value || null })}
              />
            </label>
            <label className="block space-y-1">
              <span className="text-xs font-semibold uppercase tracking-label text-on-surface-variant">
                {t('curatorTasks.fieldEnds')}
              </span>
              <input
                type="date"
                className="w-full rounded-button border border-outline-variant bg-surface px-3 py-2 text-sm"
                value={dayValue(draft.ends_on)}
                onChange={(e) => setDraft({ ends_on: e.target.value || null })}
              />
            </label>
          </div>

          {save.isError && <p className="text-sm text-error">{humanizeError(save.error)}</p>}
        </div>
  );

  // ── Телефон (docs/CURATOR-MOBILE-DESIGN.md §4.13) ─────────────────────────
  if (isMobile) {
    return (
      <div className="space-y-3">
        <MTitle
          title={t('curatorTasks.title')}
          subtitle={t('curatorTasks.subtitle')}
          right={<MAdd label={t('curatorTasks.add')} onClick={startNew} />}
        />
        <p className="px-0.5 text-[12px] text-on-surface-variant">{t('curatorTasks.note')}</p>
        {list.isLoading ? (
          <p className="px-0.5 text-[13px] text-on-surface-variant">{t('common.loading')}</p>
        ) : list.isError ? (
          <p className="px-0.5 text-[13px] text-error">{humanizeError(list.error)}</p>
        ) : rules.length === 0 ? (
          <MEmpty icon="assignment" title={t('curatorTasks.empty')} />
        ) : (
          <MBlock>
            {rules.map((rule, i) => (
              <MRow
                key={rule.id}
                wrap
                onClick={() => setEditing(rule)}
                title={rule.title}
                meta={summary(rule)}
                dim={!rule.is_active}
                aside={
                  <span className="flex flex-none items-center gap-1">
                    {!rule.is_active && <MTag tone="muted">{t('curatorTasks.inactive')}</MTag>}
                    <button
                      type="button"
                      onClick={() => setSheetFor(rule)}
                      aria-label={t('nav.more')}
                      className="grid h-11 w-11 place-items-center rounded-full text-on-surface-variant"
                    >
                      <span className="material-symbols-outlined text-[20px]">more_horiz</span>
                    </button>
                  </span>
                }
                last={i === rules.length - 1}
              />
            ))}
          </MBlock>
        )}
        {remove.isError && <p className="px-0.5 text-[13px] text-error">{humanizeError(remove.error)}</p>}
        <ActionSheet
          open={!!sheetFor}
          onClose={() => setSheetFor(null)}
          title={sheetFor?.title}
          actions={
            sheetFor
              ? [
                  { label: t('common.edit'), icon: 'edit', onSelect: () => setEditing(sheetFor) },
                  { label: t('common.delete'), icon: 'delete', danger: true, onSelect: () => confirmRemove(sheetFor) },
                ]
              : []
          }
        />
        <Modal
          open={!!draft}
          onClose={() => setEditing(null)}
          title={formTitle}
          phoneFull
          footer={<div className="grid grid-cols-2 gap-2">{cancelButton}{saveButton}</div>}
        >
          {fields}
        </Modal>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader title={t('curatorTasks.title')} subtitle={t('curatorTasks.subtitle')} />

      <Card className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-sm text-on-surface-variant">{t('curatorTasks.note')}</p>
          <Button onClick={startNew}>{t('curatorTasks.add')}</Button>
        </div>

        {list.isLoading && <p className="text-sm text-on-surface-variant">{t('common.loading')}</p>}
        {list.isError && <p className="text-sm text-error">{humanizeError(list.error)}</p>}
        {!list.isLoading && rules.length === 0 && (
          <p className="text-sm text-on-surface-variant">{t('curatorTasks.empty')}</p>
        )}

        <ul className="divide-y divide-outline-variant">
          {rules.map((rule) => (
            <li key={rule.id} className="flex flex-wrap items-center gap-3 py-3">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-on-surface">{rule.title}</p>
                <p className="text-xs text-on-surface-variant">
                  {summary(rule)}
                  {!rule.is_active && ` · ${t('curatorTasks.inactive')}`}
                </p>
              </div>
              <Button variant="ghost" onClick={() => setEditing(rule)}>{t('common.edit')}</Button>
              <Button variant="ghost" onClick={() => confirmRemove(rule)}>
                {t('common.delete')}
              </Button>
            </li>
          ))}
        </ul>
      </Card>

      {draft && (
        <Card className="space-y-4">
          <h2 className="font-display text-lg font-bold">{formTitle}</h2>
          {fields}
          <div className="flex gap-2">
            {saveButton}
            {cancelButton}
          </div>
        </Card>
      )}
    </div>
  );
}
